import React, {
  useState, useCallback, useContext, useEffect,
} from 'react';
import { useMutation } from '@apollo/client';
import ShownIn from '../../../components/Swatches/ShownIn';
import SwatchGroup from '../../../components/Swatches/SwatchGroup';
import SizeTileSection from '../../../components/SizeTileSection/SizeTileSection';
import ProductNameAndDescription from '../../../components/ProductNameAndDescription/ProductNameAndDescription';
import ProductReviews from '../../../features/ProductReviews/ProductReviews';
import BuyGridAccordionWrapper from '../../../features/BuyGridAccordions/BuyGridAccordionWrapper';
import SizeMessaging from '../../../components/SizeMessaging/SizeMessaging';
import ProductPriceSection from '../../../components/ProductPrice/ProductPrice';
import ProductPageContext from '../../../contexts/ProductPageContext';
import BreakpointContext from '../../../contexts/BreakpointContext';
import HighlightedTextWrapper from '../../../components/HighlightedTextWrapper/HighlightedTextWrapper';
import BrowserContext from '../../../contexts/BrowserContext';
import HorizontalLineSeparator from '../../../components/HorizontalSeparator/HorizontalSeparator';
import FinalSale from '../../../components/FinalSale/FinalSale';
import ESpotContent from '../../../components/ESpotContent/ESpotContent';
import ContextualFit from '../../../components/ContextualFit/ContextualFit';
import QuickviewAddToBagSection from '../QuickviewAddToBagSection/QuickviewAddToBagSection';
import useUserState from '../../../tools/UserState/useUserState';
import AnalyticsTriggers from '../../../analytics/analyticsTriggers';
import ADD_TO_BAG_MUTATION from '../../../gql/atbMutations';
import UPDATE_TO_BAG_MUTATION from '../gql/updateMutations';
import ModelMeasurement from '../../../components/ModelMeasurement/ModelMeasurement';
import useSubmitAddToBag from '../../../helpers/useAddToBagForm';
import useAddToBagErrorHandler from '../../../helpers/useAddToBagFormErrorHandler';
import AddToBagButton from '../../../components/AddToBagButton/AddToBagButton';
import AppExclusiveButton from '../../../components/AppExclusiveButton/AppExclusiveButton';
import ViewFullDetailsButton from '../../../components/ViewFullDetailsButton/ViewFullDetailsButton';
import ContentError from '../../../components/ContentError/ContentError';
import hasATBUrgencyMessage from '../../../tools/urgencyMessageTool';

export default function QuickviewAddToBagForm({
  swatchChanged,
  categoryId,
  handlePrimarySizeUpdate,
  handleSecondarySizeUpdate,
  reviewData,
  shippingContent,
  eSpotContent,
  setSizeGuideOpen,
  siteRegion,
  orderItemId,
  store,
  storeId,
  handleCloseModal,
  onModalPrimaryActionSuccessCallback,
}) {
  const ProductPageState = useContext(ProductPageContext);
  const isBrowser = useContext(BrowserContext);
  const breakpoints = useContext(BreakpointContext);
  const { isGenericUser } = useUserState(storeId);

  const {
    tmntData, collectionData, quickviewType, sku,
  } = ProductPageState;
  const { hasFitInfo, kicId } = ProductPageState.collectionData.activeProduct;
  const isQuickviewEdit = quickviewType === 'edit';
  const isFinalSale = collectionData?.activeProduct?.isFinalSale;
  const isMobile = breakpoints.xxsmall && !breakpoints.medium;
  const isLargeDesktop = breakpoints.large;
  const shouldDisplayMeasurement = hasFitInfo && !isMobile;

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [activeButtonLabel, setActiveButtonLabel] = useState(isQuickviewEdit ? 'updateToBagReady' : 'addToBagReady');
  const atbLabelText = isQuickviewEdit
    ? activeButtonLabel
    : hasATBUrgencyMessage(collectionData?.showUrgencyMessage, activeButtonLabel);

  const selectedQuantity = '1';
  const resetATBButton = useCallback(() => {
    if (isSelectedSkuSoldOut) {
      setActiveButtonLabel('addToBagSoldOut');
    } else {
      setActiveButtonLabel(isQuickviewEdit ? 'updateToBagReady' : 'addToBagReady');
    }
    setIsButtonDisabled(false);
  }, [isQuickviewEdit, isSelectedSkuSoldOut]);

  const { addToBagError, setAddToBagError, handleFormError } = useAddToBagErrorHandler(ProductPageState, resetATBButton);

  const handleMutationComplete = useCallback((data) => {
    if (data) {
      const mutationResult = isQuickviewEdit ? data.updateToBagRequest : data.addToBagRequest;

      if (mutationResult.success) {
        setActiveButtonLabel(isQuickviewEdit ? 'updateToBagSuccess' : 'addToBagAdded');
        onModalPrimaryActionSuccessCallback();
        window.dispatchEvent(new CustomEvent('addToBag:perso-instrumentation', {
          detail: {
            kicId,
          },
        }));

        setTimeout(() => {
          handleCloseModal();
          handlePostMutationActions(mutationResult.responseData);
        }, 1000);
      } else {
        const mutationError = mutationResult?.errors?.[0]?.message;
        handleFormError(mutationError);
      }
    } else {
      handleFormError(tmntData.genericCartAPIError);
    }
  }, [handleCloseModal, handleFormError, handlePostMutationActions, isQuickviewEdit, onModalPrimaryActionSuccessCallback, tmntData.genericCartAPIError, kicId]);

  const [handleBagMutation] = useMutation(isQuickviewEdit ? UPDATE_TO_BAG_MUTATION : ADD_TO_BAG_MUTATION, {
    onCompleted: (data) => handleMutationComplete(data),
    onError: () => handleFormError(tmntData?.genericCartAPIError),
  });

  const swatchHandler = useCallback((event) => {
    swatchChanged(event);
    setAddToBagError(null);
  }, [swatchChanged, setAddToBagError]);

  const primarySizeTileHandler = useCallback(({ target }) => {
    handlePrimarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handlePrimarySizeUpdate, setAddToBagError]);

  const secondarySizeTileHandler = useCallback(({ target }) => {
    handleSecondarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handleSecondarySizeUpdate, setAddToBagError]);

  useEffect(() => {
    const openedSku = sku;
    if (isQuickviewEdit && collectionData) {
      setIsButtonDisabled(!collectionData.hasQualifiedSku || collectionData.qualifiedSku === openedSku);
    }
  }, [collectionData, sku, isQuickviewEdit]);

  const { handleSubmitForm } = useSubmitAddToBag({
    onMutationSubmit: handleBagMutation,
    collectionData,
    isGenericUser,
    store,
    handleFormError,
    setActiveButtonLabel,
    isQuickviewEdit,
    orderItemId,
    tmntData,
  });

  const handlePostMutationActions = useCallback((responseData) => {
    resetATBButton();
    const checkoutCartSelector = '#checkout-cart';
    const isShoppingCart = document.querySelector('body').matches(checkoutCartSelector);
    if (isShoppingCart) {
      window.location.reload();
    } else if (isBrowser) {
      dispatchNotificationEvent(responseData);
    }
    addToBagAnalyticsEvent();
  }, [resetATBButton, isBrowser, addToBagAnalyticsEvent, dispatchNotificationEvent]);

  const dispatchNotificationEvent = useCallback((responseData) => {
    if (isLargeDesktop) {
      const miniBagEvent = new CustomEvent('miniBag-toaster:open', { detail: 'productAddedToCart' });
      window.dispatchEvent(miniBagEvent);
    } else {
      const addToBagNotificationEvent = new CustomEvent('mfe:addToBag:displayMobileNotification', { detail: responseData });
      window.dispatchEvent(addToBagNotificationEvent);
    }
  }, [isLargeDesktop]);

  const addToBagAnalyticsEvent = useCallback(() => {
    AnalyticsTriggers.addToBag(ProductPageState, { selectedQuantity });
  }, [ProductPageState, selectedQuantity]);

  const hasSelectableSoldOutSizeTiles = collectionData?.hasSelectableSoldOutSizeTiles;

  const { isSelectedSkuSoldOut } = collectionData;
  const atbLabel = isSelectedSkuSoldOut
    ? tmntData?.addToBagSoldOut?.value
    : tmntData?.[atbLabelText]?.value;

  useEffect(() => {
    if (isSelectedSkuSoldOut) {
      setActiveButtonLabel('addToBagSoldOut');
    } else {
      setActiveButtonLabel(isQuickviewEdit ? 'updateToBagReady' : 'addToBagReady');
    }
  }, [isSelectedSkuSoldOut, isQuickviewEdit]);

  if (!collectionData) {
    return null;
  }

  return (
    <>
      <div className="quickview-add-to-bag-form-wrapper" data-testid="quickview-add-to-bag-form-wrapper">
        <div className="product-page-info-container-mfe">
          {isBrowser && !isMobile ? (
            <>
              <ProductNameAndDescription isQuickView />
              <ProductPriceSection />
              <ProductReviews dataBvShow="inline_rating" />
              <HorizontalLineSeparator />
            </>
          ) : ''}

          <ShownIn />
          <SwatchGroup
            attrChangeHandler={swatchHandler}
            siteRegion={siteRegion}
          />

          {isBrowser && isMobile ? (
            <>
              <ProductPriceSection />
              <ProductReviews dataBvShow="inline_rating" />
              <HorizontalLineSeparator />
            </>
          ) : ''}

          <SizeTileSection
            primarySizeTileHandler={primarySizeTileHandler}
            secondarySizeTileHandler={secondarySizeTileHandler}
            setSizeGuideOpen={setSizeGuideOpen}
          />
          <HighlightedTextWrapper>
            <ContextualFit
              tmntData={ProductPageState.tmntData}
              reviewData={reviewData}
              noLinkText
              showIcon
            />
          </HighlightedTextWrapper>
          <SizeMessaging />

          {shouldDisplayMeasurement && (
            <ModelMeasurement
              tmntData={ProductPageState.tmntData}
              fitInformation={ProductPageState.collectionData.activeProduct.fitInformation}
            />
          )}

          { isMobile && <ContentError error={addToBagError} isDesktop={false} /> }

          {isMobile && (
            <div className="product-page-info-container-mfe__add-to-bag">
              <div className="product-page-info-container-mfe__add-to-bag__wrapper">
                {collectionData.activeProduct.isAppExclusive && !isQuickviewEdit ? (
                  <AppExclusiveButton
                    brand={collectionData.activeProduct.brand}
                    label={tmntData?.pdpOpenAppToShop?.value}
                  />
                ) : (
                  <AddToBagButton
                    brand={collectionData.activeProduct.brand}
                    label={atbLabel}
                    handleAddToBag={() => handleSubmitForm(selectedQuantity)}
                    activeButtonLabel={activeButtonLabel}
                    hasSelectableSoldOutSizeTiles={hasSelectableSoldOutSizeTiles}
                  />
                )}
              </div>
            </div>
          )}

          <div className="catalog-shop-similar__container" />

          {isFinalSale && (
            <FinalSale
              finalSaleMessage={ProductPageState.tmntData?.finalSaleText?.value}
            />
          )}

          <ESpotContent eSpotData={ProductPageState.eSpotContent?.PrimaryPDPPromoEMS} />
          <BuyGridAccordionWrapper
            categoryId={categoryId}
            reviewData={reviewData}
            shippingContent={shippingContent}
            eSpotContent={eSpotContent}
            siteRegion={siteRegion}
          />

        </div>

        {isMobile && (
          <ViewFullDetailsButton
            tmntData={tmntData}
          />
        )}
      </div>

      {!isMobile && (
        <QuickviewAddToBagSection
          isMobile={isMobile}
          activeButtonLabel={activeButtonLabel}
          addToBagError={addToBagError}
          collectionData={collectionData}
          tmntData={tmntData}
          handleSubmitForm={() => handleSubmitForm(selectedQuantity)}
          isButtonDisabled={isButtonDisabled}
          atbLabelText={atbLabel}
          hasSelectableSoldOutSizeTiles={hasSelectableSoldOutSizeTiles}
        />
      )}
    </>
  );
}
